import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { entriesApi, sitesApi, entitiesApi, subCategoriesApi, entityBankAccountsApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useOffline } from '@/contexts/OfflineContext';
import { Button } from '@/components/ui/button';
import { Plus, Upload, ChevronLeft, ChevronRight, ListPlus, FileSpreadsheet, Trash2 } from 'lucide-react';
import { EntriesFilters } from '@/components/EntriesFilters';
import { EntriesTable } from '@/components/EntriesTable';
import { EntryFormDialog } from '@/components/EntryFormDialog';
import { BulkUploadDialog } from '@/components/BulkUploadDialog';
import { AttachmentsDialog } from '@/components/AttachmentsDialog';
import { EntryDetailDialog } from '@/components/EntryDetailDialog';
import { QuickMultiEntryDialog } from '@/components/QuickMultiEntry';
import { BankStatementDialog } from '@/components/BankStatementImport';
import { toast } from 'sonner';

const DEFAULT_FORM = {
  date: new Date().toISOString().slice(0, 10),
  site_id: '', entry_type: 'expense', category: 'material', sub_category: '',
  entity_id: '', entity_name: '', payment_mode: 'cash', bank_account_id: '', bank_account_name: '', amount: '', notes: ''
};

export default function EntriesPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [siteMap, setSiteMap] = useState({});
  const [entities, setEntities] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [filters, setFilters] = useState({ site_id: '', entry_type: '', category: '', sub_category: '', payment_mode: '', is_transfer: '' });

  const { isOnline, queueAction, getCachedSites, getCachedEntities, getCachedSubCategories } = useOffline();

  // Dialog states
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formInit, setFormInit] = useState(DEFAULT_FORM);
  const [showBulk, setShowBulk] = useState(false);
  const [showQuickMulti, setShowQuickMulti] = useState(false);
  const [showBankStatement, setShowBankStatement] = useState(false);
  const [attachmentEntryId, setAttachmentEntryId] = useState(null);
  const [detailEntry, setDetailEntry] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Entity bank accounts for child dialogs
  const [entityBankAccounts, setEntityBankAccounts] = useState([]);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      if (!navigator.onLine) {
        // Load from offline cache
        const cached = await import('@/lib/offlineDB').then(m => m.offlineDB.entries.getAll());
        setEntries(cached || []);
        setTotal(cached?.length || 0);
        setPages(1);
        setLoading(false);
        return;
      }
      const params = { page, limit: 30 };
      if (filters.site_id) params.site_id = filters.site_id;
      if (filters.entry_type) params.entry_type = filters.entry_type;
      if (filters.category) params.category = filters.category;
      if (filters.sub_category) params.sub_category = filters.sub_category;
      if (filters.payment_mode) params.payment_mode = filters.payment_mode;
      if (filters.is_transfer) params.is_transfer = filters.is_transfer;
      const { data } = await entriesApi.list(params);
      setEntries(data.entries);
      setTotal(data.total);
      setPages(data.pages);
      // Cache entries for offline use
      try {
        const { offlineDB } = await import('@/lib/offlineDB');
        await offlineDB.entries.save(data.entries);
      } catch {}
    } catch (err) {
      // Try loading from offline cache on network error
      try {
        const { offlineDB } = await import('@/lib/offlineDB');
        const cached = await offlineDB.entries.getAll();
        if (cached.length > 0) {
          setEntries(cached);
          setTotal(cached.length);
          setPages(1);
        }
      } catch { /* offline fallback unavailable */ }
    }
    setLoading(false);
  }, [page, filters]);

  const fetchEntities = async () => {
    try {
      if (!navigator.onLine) {
        const cached = await getCachedEntities();
        setEntities(cached || []);
        return;
      }
      const { data } = await entitiesApi.list(); setEntities(data);
    } catch {
      try { const cached = await getCachedEntities(); setEntities(cached || []); } catch {}
    }
  };
  const fetchSubCategories = async () => {
    try {
      if (!navigator.onLine) {
        const cached = await getCachedSubCategories();
        setSubCategories(cached || []);
        return;
      }
      const { data } = await subCategoriesApi.list(); setSubCategories(data);
    } catch {
      try { const cached = await getCachedSubCategories(); setSubCategories(cached || []); } catch {}
    }
  };
  const fetchEntityBankAccountsFor = (eid) => {
    if (!eid) { setEntityBankAccounts([]); return; }
    entityBankAccountsApi.list(eid).then(r => setEntityBankAccounts(r.data)).catch(() => setEntityBankAccounts([]));
  };

  useEffect(() => {
    const loadSites = async () => {
      try {
        if (!navigator.onLine) {
          const cached = await getCachedSites();
          setSites(cached || []);
          const map = {};
          (cached || []).forEach(s => { map[s._id] = s.name; });
          setSiteMap(map);
          return;
        }
        const r = await sitesApi.list();
        setSites(r.data);
        const map = {};
        r.data.forEach(s => { map[s._id] = s.name; });
        setSiteMap(map);
      } catch {
        try {
          const cached = await getCachedSites();
          setSites(cached || []);
          const map = {};
          (cached || []).forEach(s => { map[s._id] = s.name; });
          setSiteMap(map);
        } catch {}
      }
    };
    loadSites();
    fetchEntities();
    fetchSubCategories();
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'new') { setFormInit(DEFAULT_FORM); setEditing(null); setShowForm(true); setSearchParams({}); }
    if (action === 'bulk') { setShowBulk(true); setSearchParams({}); }
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (form, editingId) => {
    const payload = { ...form, amount: parseFloat(form.amount) };
    try {
      if (!navigator.onLine) {
        // Queue for later sync
        if (editingId) {
          await queueAction({ type: 'UPDATE_ENTRY', targetId: editingId, payload });
        } else {
          await queueAction({ type: 'CREATE_ENTRY', payload });
        }
        toast.success('Saved offline — will sync when back online');
        setShowForm(false);
        setEditing(null);
        setFormInit(DEFAULT_FORM);
        return;
      }
      if (editingId) { await entriesApi.update(editingId, payload); }
      else { await entriesApi.create(payload); }
      setShowForm(false);
      setEditing(null);
      setFormInit(DEFAULT_FORM);
      fetchEntries();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save entry'); }
  };

  const handleEdit = (entry) => {
    setFormInit({
      date: entry.date, site_id: entry.site_id, entry_type: entry.entry_type,
      category: entry.category, sub_category: entry.sub_category || '',
      entity_id: entry.entity_id || '', entity_name: entry.entity_name || '',
      payment_mode: entry.payment_mode || 'cash',
      bank_account_id: entry.bank_account_id || '', bank_account_name: entry.bank_account_name || '',
      amount: String(entry.amount), notes: entry.notes || ''
    });
    setEditing(entry._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    if (!navigator.onLine) {
      await queueAction({ type: 'DELETE_ENTRY', targetId: id });
      toast.success('Delete queued — will sync when back online');
      setEntries(prev => prev.filter(e => e._id !== id));
      return;
    }
    await entriesApi.delete(id);
    fetchEntries();
  };

  const handleCopy = async (entry) => {
    try { await entriesApi.copy(entry._id); fetchEntries(); } catch (err) { toast.error('Failed to copy entry'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === entries.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(entries.map(e => e._id)));
  };
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected entries? They will be moved to Trash.`)) return;
    try {
      await entriesApi.bulkDelete([...selectedIds]);
      toast.success(`${selectedIds.size} entries deleted`);
      setSelectedIds(new Set());
      fetchEntries();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Bulk delete failed'); }
  };

  const isEditor = ['admin', 'manager'].includes(user?.role);

  return (
    <div className="space-y-6" data-testid="entries-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Entries</h1>
          <p className="text-sm text-muted-foreground mt-1">{total} total entries</p>
        </div>
        {isEditor && (
          <div className="flex gap-2 flex-wrap items-center">
            {selectedIds.size > 0 && (
              <Button variant="destructive" size="sm" className="gap-1.5" onClick={handleBulkDelete} data-testid="bulk-delete-btn">
                <Trash2 className="w-4 h-4" /> Delete {selectedIds.size} Selected
              </Button>
            )}
            <Button variant="outline" className="gap-1.5" onClick={() => setShowBankStatement(true)} data-testid="import-statement-btn">
              <FileSpreadsheet className="w-4 h-4" /> Import Statement
            </Button>
            <Button variant="outline" className="gap-1.5" onClick={() => setShowQuickMulti(true)} data-testid="quick-multi-btn">
              <ListPlus className="w-4 h-4" /> Quick Multi
            </Button>
            <Button variant="outline" className="gap-1.5" onClick={() => setShowBulk(true)} data-testid="bulk-upload-btn">
              <Upload className="w-4 h-4" /> CSV Upload
            </Button>
            <Button className="gap-1.5" onClick={() => { setFormInit(DEFAULT_FORM); setEditing(null); setShowForm(true); }} data-testid="add-entry-btn">
              <Plus className="w-4 h-4" /> Add Entry
            </Button>
          </div>
        )}
      </div>

      <EntriesFilters filters={filters} setFilters={setFilters} sites={sites} subCategories={subCategories} />

      <EntriesTable
        entries={entries}
        userRole={user?.role}
        siteMap={siteMap}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onCopy={handleCopy}
        onOpenAttachments={setAttachmentEntryId}
        onRowClick={setDetailEntry}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAll}
      />

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm text-muted-foreground">Page {page} of {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} data-testid="next-page"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      )}

      {/* Dialogs */}
      <EntryFormDialog
        open={showForm}
        onOpenChange={setShowForm}
        editing={editing}
        initialForm={formInit}
        sites={sites}
        entities={entities}
        subCategories={subCategories}
        onSubmit={handleSubmit}
        onEntitiesChanged={fetchEntities}
        onSubCategoriesChanged={fetchSubCategories}
      />

      <BulkUploadDialog open={showBulk} onOpenChange={setShowBulk} onSuccess={fetchEntries} />

      <AttachmentsDialog entryId={attachmentEntryId} onClose={() => setAttachmentEntryId(null)} userRole={user?.role} />

      <EntryDetailDialog
        entry={detailEntry}
        siteName={detailEntry ? siteMap[detailEntry.site_id] : ''}
        open={!!detailEntry}
        onOpenChange={(open) => { if (!open) setDetailEntry(null); }}
      />

      <QuickMultiEntryDialog
        open={showQuickMulti}
        onOpenChange={setShowQuickMulti}
        sites={sites}
        entities={entities}
        subCategories={subCategories}
        entityBankAccounts={entityBankAccounts}
        fetchEntityBankAccounts={fetchEntityBankAccountsFor}
        onSuccess={fetchEntries}
      />

      <BankStatementDialog
        open={showBankStatement}
        onOpenChange={setShowBankStatement}
        sites={sites}
        entities={entities}
        entityBankAccounts={entityBankAccounts}
        fetchEntityBankAccounts={fetchEntityBankAccountsFor}
        onSuccess={fetchEntries}
      />
    </div>
  );
}
